import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { resetLastTimeStoreUpdated } from 'features/store/hooks/reset-last-time-store-updated';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useIntl } from 'react-intl';
import { Alert } from 'react-native';

import { IBaseProps, Nullable } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
// TODO: This is the original DeliveryFeeSurchargeDialog, we need to use this once the useDialogModal
// renders ok within a modal screen (after GST-5015 is done)
// import useDialogModal from 'hooks/use-dialog-modal';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import {
  CustomEventNames,
  EventTypes,
  logRBIEvent,
  updateUniversalAttributes,
} from 'state/amplitude';
import { serializePickupMode, serializeServiceMode } from 'state/amplitude/utils';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';
import {
  getHeadingForServiceMode,
  getPreferredDriveThruServiceMode,
  getServiceModeDescriptionTextId,
} from 'utils/service-mode';
import { useMemoAll } from 'utils/use-memo-all';

import { SERVICE_MODE_ORDER, serviceModeImages } from './constants';
import { IServiceModeCtx, ServiceModeOption } from './types';

const getServiceModeFromLS = (): ServiceMode | null => {
  const preloaded = LocalStorage.getItem(StorageKeys.SERVICE_MODE);
  return preloaded ? preloaded.serviceMode : null;
};

const persistServiceMode = (serviceMode: Nullable<ServiceMode>) => {
  LocalStorage.setItem(StorageKeys.SERVICE_MODE, { serviceMode });
};

export const ServiceModeContext = createContext<IServiceModeCtx>({
  serviceMode: null,
  selectServiceMode: noop,
  setDeliverySurchargeFee: noop,
  isDelivery: false,
  isTakeout: false,
  isEatIn: false,
  isMobileOrderForDriveThru: false,
  isDriveThru: false,
  getServiceModeOptions: () => [],
});

export const useServiceModeContext = () => useContext(ServiceModeContext);

export const ServiceModeProvider = ({ children }: IBaseProps) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const mobileOrderDriveThruStores = useFlag(LaunchDarklyFlag.MOBILE_ORDER_DRIVE_THRU_STORES);

  const { isServiceModeAvailable } = useStoreAvailabilityFns();
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );

  const { params } = useRoute<{ 'service-mode': string }>();
  const serviceModeInRoute = ServiceMode[params['service-mode']] ?? '';

  const [serviceMode, setServiceMode] = useState<ServiceMode | null>(
    serviceModeInRoute || getServiceModeFromLS()
  );
  const enableDeliveryOnly = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const enableDeliveryFeeSurchargeModal = Boolean(
    useFlag(LaunchDarklyFlag.SHOW_INCREASED_DELIVERY_FEES_MODAL)
  );

  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const isTakeout = serviceMode === ServiceMode.TAKEOUT;
  const isEatIn = serviceMode === ServiceMode.EAT_IN;
  const isMobileOrderForDriveThru = serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU;
  const isDriveThru = serviceMode === ServiceMode.DRIVE_THRU;
  const { updateLDUser } = useLDContext();

  const [
    deliverySurchargeFee,
    setDeliverySurchargeFee,
    clearDeliverySurchargeFee,
  ] = useLocalStorageState<number | null>({
    key: StorageKeys.DELIVERY_SURCHARGE_FEE,
    defaultReturnValue: null,
  });

  // TODO: This is the original DeliveryFeeSurchargeDialog, we need to use this once the useDialogModal
  // renders ok within a modal screen (after GST-5015 is done)
  //
  // const [DeliveryFeeSurchargeDialog, openDeliveryFeeSurcharge] = useDialogModal({
  //   onConfirm: acceptDeliverySurchargeFee,
  //   allowDismiss: false,
  //   modalAppearanceEventMessage: 'Delivery Surcharge',
  // });

  const showDeliveryFeeSurchargeModal = enableDeliveryFeeSurchargeModal && !!deliverySurchargeFee;

  const acceptDeliverySurchargeFee = useCallback(() => {
    clearDeliverySurchargeFee();
  }, [clearDeliverySurchargeFee]);

  const selectServiceMode = useCallback((serviceMode: ServiceMode | null) => {
    setServiceMode(serviceMode);
    logRBIEvent({
      name: CustomEventNames.SELECT_SERVICE_MODE,
      type: EventTypes.Other,
      attributes: null,
    });
    resetLastTimeStoreUpdated();
  }, []);

  const openDeliveryFeeSurcharge = useCallback(() => {
    // TODO: Change this native dialog with DeliveryFeeSurchargeDialog once GST-5015 is done
    Alert.alert(
      formatMessage({ id: 'deliverySurcharge' }),
      formatMessage(
        { id: 'deliverySurchargeMessage' },
        {
          feeSurcharge: currencyFormatter(deliverySurchargeFee || 0),
        }
      ),
      [
        {
          text: formatMessage({ id: 'okay' }),
          onPress: () => acceptDeliverySurchargeFee(),
        },
      ]
    );
  }, [formatMessage, currencyFormatter, deliverySurchargeFee, acceptDeliverySurchargeFee]);

  useEffect(() => {
    if (showDeliveryFeeSurchargeModal) {
      openDeliveryFeeSurcharge();
    }
  }, [openDeliveryFeeSurcharge, showDeliveryFeeSurchargeModal]);

  useEffect(() => {
    persistServiceMode(serviceMode);
    // Updates service/pickup modes for all amplitude events/page views
    updateUniversalAttributes({
      'Service Mode': serializeServiceMode(serviceMode),
      'Pickup Mode': serializePickupMode(serviceMode),
    });
    updateLDUser({ serviceMode: serviceMode ?? '' });
  }, [serviceMode, updateLDUser]);

  useEffect(() => {
    // If the service mode isn't delivery and the only service mode available is delivery, wipe out the current service mode
    if (enableDeliveryOnly && !isDelivery) {
      setServiceMode(null);
    }
  }, [enableDeliveryOnly, isDelivery, serviceMode]); // Needed to be checked every time serviceMode is being changed

  useEffect(() => {
    if (serviceModeInRoute && serviceModeInRoute !== serviceMode) {
      setServiceMode(serviceModeInRoute);
    }
  }, [serviceMode, serviceModeInRoute]);

  /**
   * Returns the service mode options for the given store.
   *
   * @param store - The store to get the service mode options for.
   * @returns The service mode options for the given store.
   */
  const getServiceModeOptions = useCallback(
    (store: IRestaurant): ServiceModeOption[] => {
      const isMobileOrderDriveThruEnabledForStore =
        mobileOrderDriveThruStores?.split(',').includes(store.number ?? '') ?? false;

      return SERVICE_MODE_ORDER.flatMap(serviceModeOption => {
        if (
          serviceModeOption === ServiceMode.DRIVE_THRU ||
          serviceModeOption === ServiceMode.MOBILE_ORDER_DRIVE_THRU
        ) {
          const preferredDriveThruServiceMode = getPreferredDriveThruServiceMode(
            isMobileOrderDriveThruEnabledForStore,
            isServiceModeAvailable,
            store
          );

          if (serviceModeOption !== preferredDriveThruServiceMode) {
            return [];
          }
        }

        const serviceModeHeadingAndIcon = getHeadingForServiceMode(
          formatMessage,
          isFastLaneEnabled,
          isMobileOrderForDriveThruLaneEnabled,
          serviceModeOption
        );

        const descriptionTextId = getServiceModeDescriptionTextId(serviceModeOption);
        return {
          icon: serviceModeHeadingAndIcon?.icon ?? '',
          heading: serviceModeHeadingAndIcon?.heading ?? '',
          name: serviceModeOption,
          image: serviceModeImages[serviceModeOption],
          description: formatMessage({ id: descriptionTextId }),
          isSelected: serviceMode === serviceModeOption,
          isAvailable: isServiceModeAvailable(store, serviceModeOption),
        };
      });
    },
    [
      formatMessage,
      isFastLaneEnabled,
      isMobileOrderForDriveThruLaneEnabled,
      serviceMode,
      isServiceModeAvailable,
      mobileOrderDriveThruStores,
    ]
  );

  const value = useMemoAll({
    serviceMode,
    selectServiceMode,
    setDeliverySurchargeFee,
    isDelivery,
    isTakeout,
    isEatIn,
    isMobileOrderForDriveThru,
    isDriveThru,
    getServiceModeOptions,
  });

  return (
    <ServiceModeContext.Provider value={value}>
      {/*
      TODO: This is the original DeliveryFeeSurchargeDialog, we need to use this once the useDialogModal
      renders ok within a modal screen (after GST-5015 is done)

      {enableDeliveryFeeSurchargeModal && deliverySurchargeFee && (
        <DeliveryFeeSurchargeDialog
          heading={formatMessage({ id: 'deliverySurcharge' })}
          body={formatMessage(
            { id: 'deliverySurchargeMessage' },
            {
              feeSurcharge: currencyFormatter(deliverySurchargeFee),
            }
          )}
        />
      )} */}

      {children}
    </ServiceModeContext.Provider>
  );
};
